import { PaginationData, QueryParams } from 'shared/types';
import { Invoice } from 'types';
import request from 'features/documents/services/requesters/document.request';

// Interface for create mode parameters
interface CreateModeParams {
  prestataire?: string;
  [key: string]: string | number | boolean | undefined;
}

// Interface for edit mode parameters
interface EditModeParams {
  prestataire?: string;
  invoiceId?: string | number;
  [key: string]: unknown;
}

const providerInvoiceService = {
  getInvoicesList: async (
    params?: QueryParams & { serviceProviderId?: string | number },
  ): Promise<PaginationData<Invoice>> => {
    try {
      // Prepare query parameters for the API
      const queryParams: Record<string, string | number> = {};

      if (params?.serviceProviderId) {
        queryParams.serviceProviderId = params.serviceProviderId;
      }

      if (params?.search) {
        queryParams.search = params.search;
      }

      if (params?.page) {
        queryParams.page = params.page;
      }

      if (params?.limit && params.limit !== 'unlimited') {
        queryParams.limit = params.limit;
      } else {
        queryParams.limit = 10; // Default limit
      }

      // Call the real API endpoint
      const response = await request.get<PaginationData<Invoice>>('/invoices', { params: queryParams });
      return response;
    } catch (error: unknown) {
      console.error('❌ Invoice API failed:', error);
      // Return empty result on error
      return {
        rows: [],
        count: 0,
      };
    }
  },

  getInvoiceLinesByProviderIdCreate: async (
    params: CreateModeParams & { serviceProviderId?: string | number },
  ): Promise<PaginationData<unknown>> => {
    const { serviceProviderId } = params;

    try {
      // Call the real API endpoint to get provider invoice data by serviceProviderId
      const response = await request.get<unknown>(`/get-provider-invoice-data/${serviceProviderId}`);

      // Return in the expected format
      return {
        rows: Array.isArray(response) ? response : [response],
        count: Array.isArray(response) ? response.length : 1,
      };
    } catch (error: unknown) {
      console.error('❌ Invoice API failed:', error);
      // Return empty result on error
      return {
        rows: [],
        count: 0,
      };
    }
  },

  getInvoiceDetailsByProviderIdEdit: async (params: EditModeParams): Promise<PaginationData<unknown>> => {
    const { invoiceId } = params;

    try {
      // Call the real API endpoint to get invoice details by documentId
      const response = await request.get<unknown>(`/invoice/${invoiceId}`);

      // Return in the expected format
      return {
        rows: Array.isArray(response) ? response : [response],
        count: Array.isArray(response) ? response.length : 1,
      };
    } catch (error: unknown) {
      console.error('❌ Invoice Details API failed:', error);
      // Return empty result on error
      return {
        rows: [],
        count: 0,
      };
    }
  },

  createOrUpdateInvoice: async (invoiceData: unknown): Promise<unknown> => {
    try {
      const response = await request.post('/create-or-update-invoice', invoiceData);
      return response;
    } catch (error: unknown) {
      console.error('❌ Invoice save API failed:', error);
      throw error;
    }
  },
};

export default providerInvoiceService;
